import { useState } from "react";
import { Play } from "lucide-react";
import { track } from "@/lib/marketing/analytics";

export type ReplaySource = { kind: "iframe"; src: string } | { kind: "video"; src: string } | { kind: "external"; src: string };

/**
 * Turns an admin-entered replay URL into something embeddable. YouTube,
 * Vimeo and Loom share links become their player URLs (autoplay, since the
 * player only loads after the visitor presses play); Google Drive file links
 * become Drive's /preview player; direct video files play
 * in a <video>. PadPulse links open externally, rather than nesting the
 * app inside its own player. Other URLs retain custom-player support.
 */
export function replaySource(raw: string): ReplaySource | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const host = url.hostname.replace(/^www\.|^m\./, "");
  if (host === "padpulse.io") return { kind: "external", src: url.toString() };
  if (host === "youtube.com" || host === "youtu.be" || host === "youtube-nocookie.com") {
    const id =
      host === "youtu.be"
        ? url.pathname.slice(1).split("/")[0]
        : url.searchParams.get("v") ?? url.pathname.match(/^\/(?:embed|live|shorts)\/([^/?]+)/)?.[1];
    return id ? { kind: "iframe", src: `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0` } : null;
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const id = url.pathname.match(/(\d{5,})/)?.[1];
    return id ? { kind: "iframe", src: `https://player.vimeo.com/video/${id}?autoplay=1` } : null;
  }
  if (host === "loom.com") {
    const id = url.pathname.match(/^\/(?:share|embed)\/([^/?]+)/)?.[1];
    return id ? { kind: "iframe", src: `https://www.loom.com/embed/${encodeURIComponent(id)}?autoplay=1` } : null;
  }
  if (host === "drive.google.com") {
    const id = url.pathname.match(/^\/file\/d\/([^/?]+)/)?.[1] ?? (url.pathname === "/open" || url.pathname === "/uc" ? url.searchParams.get("id") : null);
    return id ? { kind: "iframe", src: `https://drive.google.com/file/d/${encodeURIComponent(id)}/preview` } : null;
  }
  if (/\.(mp4|webm|m3u8|mov)$/i.test(url.pathname)) return { kind: "video", src: url.toString() };
  return { kind: "iframe", src: url.toString() };
}

/**
 * "Missed the webinar?" replay section on /syre (F7 v2, new in v4). The
 * player is lazy: nothing third-party loads until the visitor presses play,
 * which is also when `syre_webinar_played` fires (once).
 */
export function SyreWebinarSection({ url, title }: { url: string; title: string | null }) {
  const [playing, setPlaying] = useState(false);
  const source = replaySource(url);
  if (!source) return null;

  function play() {
    if (!playing) track("syre_webinar_played");
    setPlaying(true);
  }

  return (
    <section id="webinar" className="scroll-mt-24 bg-gray-50 px-4 py-12" data-testid="section-syre-webinar">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-2xl font-bold text-gray-900">Full webinar from October 22nd</h2>
        <p className="mt-2 text-gray-700">
          {source.kind === "external" ? "Open the webinar link in a new tab." : "The 1-hour Scale Your Real Estate session. Press play to watch it here, or open it in a new tab."}
        </p>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="mt-5 inline-flex rounded-lg bg-brand px-5 py-3 font-semibold text-white transition hover:bg-brand-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          data-testid="link-syre-webinar-watch"
        >
          Open webinar link
        </a>
        {source.kind !== "external" && <div className="relative mt-6 aspect-video w-full overflow-hidden rounded-xl bg-[#0a1628] shadow-lg">
          {playing ? (
            source.kind === "video" ? (
              <video src={source.src} controls autoPlay playsInline className="h-full w-full" data-testid="video-syre-webinar" />
            ) : (
              <iframe
                src={source.src}
                title={title ?? "PadPulse webinar replay"}
                className="h-full w-full"
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
                data-testid="iframe-syre-webinar"
              />
            )
          ) : (
            <button
              type="button"
              onClick={play}
              className="group absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-3 text-white focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-[-8px] focus-visible:outline-white"
              aria-label="Play the Scale Your Real Estate webinar replay from October 22nd"
              data-testid="button-syre-webinar-play"
            >
              <img
                src="/images/syre-webinar-poster.svg"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="image-syre-webinar-poster"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-[#071426]/55 via-transparent to-[#071426]/10 transition group-hover:from-[#071426]/65" />
              <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-white text-brand shadow-[0_8px_30px_rgba(0,0,0,0.35)] transition group-hover:scale-110">
                <Play className="ml-1 h-7 w-7" fill="currentColor" />
              </span>
              <span className="relative rounded-full border border-white/50 bg-[#071426]/55 px-4 py-2 text-sm font-semibold text-white shadow-sm backdrop-blur-sm">
                Watch the replay
              </span>
            </button>
          )}
        </div>}
      </div>
    </section>
  );
}
